import { useCallback, useMemo, useEffect } from 'react'
import { IAddOpportunityFormModel } from '@user/components/Opportunities/AddOpportunityPage/typings'
import { useFormikContext, useField } from 'formik'
import { ENTITY_TYPES } from '@sales-center/common'
import {
  ISalesFunnelStage,
  useSelectFunnelStagesByType,
} from '@user/store/salesFunnel'
import { useTidalThemeActive } from '@user/features/TidalThemeToggle'

export function useData({ isCloseableDetails, name, required }) {
  const tidalThemeActive = useTidalThemeActive()

  const {
    setFieldValue,
    isSubmitting,
    values: { opportunityStageId, salesFunnelId },
  } = useFormikContext<IAddOpportunityFormModel>()

  const [{}, { error }] = useField({
    name,
    validate: (value) => {
      if (required !== undefined ? required : true && !value) {
        return 'Required'
      }
    },
  })

  const showError = (required == undefined || required == true) && !!error
  const helperText =
    (required == undefined || required == true) && showError ? error : ''

  const salesFunnelStages = useSelectFunnelStagesByType(
    +salesFunnelId,
    ENTITY_TYPES.Opportunity,
  )

  const options = useMemo(() => salesFunnelStages, [salesFunnelStages])
  const selectedValue = useMemo(
    () => options?.find((row) => row.id == parseInt(opportunityStageId)),
    [options, opportunityStageId],
  )

  const isCloseWonSelected = useMemo(() => {
    if (selectedValue && selectedValue.name == 'Closed/Won') {
      return true
    }
    return false
  }, [selectedValue])

  const getOptionLabel = useCallback(
    (option: ISalesFunnelStage) => option?.name,
    [],
  )

  const getId = useCallback((option: ISalesFunnelStage) => {
    return String(option.id)
  }, [])

  const getOptionDisabled = useCallback((option: ISalesFunnelStage) => {
    return !isCloseableDetails?.closeable && option.name == 'Closed/Won'
  }, [])

  useEffect(() => {
    if (!opportunityStageId && options?.length == 1) {
      setFieldValue(name, options[0].id)
    }
  }, [opportunityStageId, setFieldValue])

  const initialOption = useMemo(() => {
    if (opportunityStageId) {
      const value = options.find((x) => x.id == parseInt(opportunityStageId))
      if (value) return value
    }
    return null
  }, [options, opportunityStageId])

  const notCloseableMessage =
    !isCloseableDetails || isCloseableDetails.closeable
      ? ''
      : 'Closing requirements have not been met. ' +
        isCloseableDetails.requirements.map((r) => r.reason).join(' ')

  const height = tidalThemeActive ? '50px' : '33px'

  return {
    getOptionLabel,
    getId,
    options,
    initialOption,
    getOptionDisabled,
    notCloseableMessage,
    isSubmitting,
    showError,
    helperText,
    isCloseWonSelected,
    height,
  }
}
